/**
 * Générateur — Automatisme : reconnaître des droites remarquables, y compris
 * dans les triangles particuliers (médiatrices, médianes, hauteurs, bissectrices).
 * Attendu ID : 1124 | Niveau : 4ème | Domaine : Géométrie plane
 * Rubrique : AUTOMATISME (programme 2026, Triangles 4e).
 * Labels (cartes exerciseur) :
 *   - Quelle droite ?                (QCM : un triangle, une droite codée → hauteur, médiane, médiatrice, bissectrice ;
 *     QCM planche : quatre fois le même triangle, sur quelle figure la droite est-elle … ? — carte FUSIONNÉE, décision prof 2026-09-12)
 *   - Dans un triangle particulier   (QCM : isocèle, équilatéral, rectangle — ce que devient la droite)
 *   - Vrai ou faux ?                 (V/F sur une figure codée : nature de la droite, ce que dit le codage)
 *   - Les droites remarquables : vrai ou faux ? (V/F de pur rappel)
 *
 * ── Plan de cartes (feu vert donné d'avance, commande du 2026-09-10) ────────
 * Source : BO cycle 4 (2026), Triangles 4e + grille P1-P16.
 * A. P1 : CM2 introduit (perpendiculaire, milieu, angle droit, codage), 6e
 * formalisé (médiatrice d'un segment, bissectrice d'un angle : définitions et
 * constructions), 5e étendu (médiatrice et cercle circonscrit en automatisme ;
 * hauteurs et médianes DÉFINIES et tracées ; bissectrice reconnue), 4e
 * automatisme : reconnaître laquelle des quatre est tracée, y compris quand
 * elles se confondent (triangle isocèle, équilatéral) ou se confondent avec un
 * côté (triangle rectangle). P2 : sens installés — chaque droite par sa
 * définition (hauteur : par un sommet, perpendiculaire au côté opposé ;
 * médiane : par un sommet et le milieu du côté opposé ; médiatrice :
 * perpendiculaire au côté en son milieu ; bissectrice : partage l'angle en
 * deux angles de même mesure). Sens nouveau : dans un triangle particulier
 * une même droite cumule plusieurs natures. Distracteurs : hauteur ↔ médiane
 * (le pied n'est pas le milieu), médiane ↔ médiatrice (par le sommet ou non),
 * médiatrice ↔ hauteur (le milieu ou non), bissectrice ↔ médiane (l'angle ou
 * le côté). P3 : aucun nombre. P4 : sans objet.
 * B. P5 : triangle sur feuille blanche, la droite tracée en couleur, codages
 * cycle 3 (traits d'égalité, carré d'angle droit, arcs d'angles égaux). P6 :
 * aucun instrument. P7 : aucune mesure, le codage fait foi. P8 : hauteur,
 * médiane, médiatrice, bissectrice, milieu, perpendiculaire, (d), [BC].
 * C. P9 : lecture des codages (6e), médiatrice (1078), bissectrice (1074),
 * triangles particuliers par codage (5e). P10 : « que représente (d) ? » sur
 * codage, aucune mesure. P11 : dans un triangle quelconque les quatre droites
 * issues d'un même sommet sont DISTINCTES (le dessin les sépare nettement) ;
 * dans un triangle isocèle elles sont confondues pour le sommet principal ;
 * dans un triangle rectangle deux hauteurs sont des côtés. P12 : rémanence
 * forte (5e → 4e).
 * D. P13 : leurres = les trois autres droites, toujours nommées avec le même
 * sommet ou le même côté ; pour les triangles particuliers, « seulement la
 * médiane », « la médiane mais pas la hauteur », le côté [BC] au lieu de
 * [BA]. P14 : aucun calcul. P15 : UNICITÉ — triangle scalène et acutangle
 * construit avec |angle B − angle C| ≥ 15°, donc pied de la hauteur, milieu
 * et pied de la bissectrice distincts ; une figure porte UN codage ; en
 * triangle particulier la réponse est la liste complète. P16 : QCM à quatre
 * droites nommées ; QCM planche numérotée 1 à 4 ; V/F sans crochets dans le rappel.
 * INVARIANTS : le dessin est CONSTRUIT (pied, milieu, bissectrice calculés) ;
 * data-droite / data-sommet / data-config sur la figure pour les solveurs ;
 * V/F mixité 1-3 faux, familles exclusives.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux,
    retirerCodesErreur,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { creerFigure, cadreur, milieu, dist, angleVers, pied, codageDroitInterieur } from '../../5eme/triangles/svgTriangles';

const ATTENDU_ID = 1124;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DROITE = 'Quelle droite ?';
const L_PARTICULIER = 'Dans un triangle particulier';
const L_VF = 'Vrai ou faux ?';
const L_RAPPEL = 'Les droites remarquables : vrai ou faux ?';

const DROITES = ['hauteur', 'mediane', 'mediatrice', 'bissectrice'];
/** Nom d'un segment ou d'une droite : lettres dans l'ordre alphabétique ([BC], jamais [CB]). */
const seg = (P, Q) => [P, Q].sort().join('');
/** Nom complet d'une droite, pour le sommet S et le côté opposé [PQ] (lettres). */
const nomDroite = (d, S, P, Q) => ({
    hauteur: `La hauteur issue de ${S}`,
    mediane: `La médiane issue de ${S}`,
    mediatrice: `La médiatrice du segment [${seg(P, Q)}]`,
    bissectrice: `La bissectrice de l'angle ${P}${S}${Q}`,
}[d]);

// ─── Triangle scalène acutangle (repère écran) ─────────────────────────────
const rot = (P, deg) => { const r = (deg * Math.PI) / 180; return { x: P.x * Math.cos(r) - P.y * Math.sin(r), y: P.x * Math.sin(r) + P.y * Math.cos(r) }; };
/**
 * Sommet principal S en haut, base [P Q]. Le pied de la hauteur, le milieu et
 * le pied de la bissectrice sont nettement séparés : |xS − L/2| ≥ 0,14 L.
 */
const triangleQuelconque = () => {
    const L = 100; const h = randInt(62, 88);
    const xS = randomChoice([randInt(14, 30), randInt(70, 86)]);
    const pts = { S: { x: xS, y: -h }, P: { x: 0, y: 0 }, Q: { x: L, y: 0 } };
    const deg = randomChoice([-9, -5, 0, 4, 8]);
    return Object.fromEntries(Object.entries(pts).map(([k, v]) => [k, rot(v, deg)]));
};
const VB = { w: 300, h: 240 };
const ORANGE = '#b45309';

const traits = (fig, a, b, n) => {
    const M = milieu(a, b); const d = dist(a, b) || 1; const ux = (b.x - a.x) / d; const uy = (b.y - a.y) / d; const nx = -uy; const ny = ux;
    for (let k = 0; k < n; k++) {
        const t = (k - (n - 1) / 2) * 5; const cx = M.x + ux * t; const cy = M.y + uy * t;
        fig.brut(`<line class="tk" x1="${(cx + nx * 5).toFixed(1)}" y1="${(cy + ny * 5).toFixed(1)}" x2="${(cx - nx * 5).toFixed(1)}" y2="${(cy - ny * 5).toFixed(1)}" stroke="#0f172a" stroke-width="1.8"/>`);
    }
};
/** Arc d'angle égal (un trait) au sommet V entre les directions vers P et Q (secteur intérieur). */
const arcEgal = (fig, V, P, Q, r = 17) => {
    const a1 = Math.atan2(P.y - V.y, P.x - V.x); const a2 = Math.atan2(Q.y - V.y, Q.x - V.x);
    let d = a2 - a1; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI;
    let path = '';
    for (let k = 0; k <= 10; k++) { const a = a1 + (d * k) / 10; path += `${k ? 'L' : 'M'} ${(V.x + r * Math.cos(a)).toFixed(1)},${(V.y + r * Math.sin(a)).toFixed(1)} `; }
    fig.brut(`<path class="ar" d="${path}" fill="none" stroke="#0f172a" stroke-width="1.5"/>`);
    const am = a1 + d / 2;
    fig.brut(`<line x1="${(V.x + (r - 4) * Math.cos(am)).toFixed(1)}" y1="${(V.y + (r - 4) * Math.sin(am)).toFixed(1)}" x2="${(V.x + (r + 4) * Math.cos(am)).toFixed(1)}" y2="${(V.y + (r + 4) * Math.sin(am)).toFixed(1)}" stroke="#0f172a" stroke-width="1.5"/>`);
};

/** Étiquette « (d) » près du bord, à côté de la droite (décalée de 13 px pour ne pas la chevaucher). */
const etiquetteDroite = (fig, origine, dirDeg, txt) => {
    const r = (dirDeg * Math.PI) / 180; const ux = Math.cos(r); const uy = -Math.sin(r);
    // point de la droite à 12 px du bord vers lequel elle sort
    let t;
    if (Math.abs(uy) >= 0.3) t = ((uy > 0 ? VB.h - 12 : 12) - origine.y) / uy;
    else t = ((ux > 0 ? VB.w - 12 : 12) - origine.x) / ux;
    const E = { x: origine.x + ux * t, y: origine.y + uy * t };
    const nx = -uy; const ny = ux;
    fig.brut(`<text class="rl" x="${(E.x + nx * 13).toFixed(1)}" y="${(E.y + ny * 13 + 4).toFixed(1)}" font-family="serif" font-style="italic" font-size="14" fill="${ORANGE}" text-anchor="middle">${txt}</text>`);
};

/**
 * Figure : triangle nommé (lettres = noms[S], noms[P], noms[Q]) avec la droite
 * remarquable `droite` relative au sommet S / côté [PQ]. Renvoie { svg, ... }.
 */
const figureDroite = (T, noms, droite, opt = {}) => {
    const cad = cadreur([T.S, T.P, T.Q], VB, 48);
    const S = cad(T.S); const P = cad(T.P); const Q = cad(T.Q);
    const fig = creerFigure(VB, `1124_${droite}_${randInt(1, 999)}`);
    fig.segment(S, P); fig.segment(P, Q); fig.segment(Q, S);
    const I = milieu(P, Q); const H = pied(S, P, Q);
    const dirBase = angleVers(P, Q);
    let dirLigne; let origine;
    if (droite === 'hauteur') { origine = S; dirLigne = angleVers(S, H); }
    else if (droite === 'mediane') { origine = S; dirLigne = angleVers(S, I); }
    else if (droite === 'mediatrice') { origine = I; dirLigne = dirBase + 90; }
    else { // bissectrice : direction moyenne des deux côtés issus de S
        const a1 = angleVers(S, P); const a2 = angleVers(S, Q); let dd = a2 - a1; while (dd > 180) dd -= 360; while (dd < -180) dd += 360;
        origine = S; dirLigne = a1 + dd / 2;
    }
    // la droite (recolorée en orange après rendu : creerFigure trace en gris ardoise)
    if (opt.droiteVisible !== false) fig.droite(origine, dirLigne, 'dr', 1.8);
    // codages
    if (droite === 'hauteur') { codageDroitInterieur(fig, H, Q, S, { couleur: '#0f172a', c: 9 }); }
    if (droite === 'mediane') { traits(fig, P, I, 1); traits(fig, I, Q, 1); fig.point(I, opt.nomMilieu || '', dirBase + 90 + 180, 13); }
    if (droite === 'mediatrice') { traits(fig, P, I, 1); traits(fig, I, Q, 1); const N = { x: I.x + Math.cos((dirLigne * Math.PI) / 180) * 30, y: I.y - Math.sin((dirLigne * Math.PI) / 180) * 30 }; codageDroitInterieur(fig, I, Q, N, { couleur: '#0f172a', c: 9 }); }
    if (droite === 'bissectrice') {
        const M = { x: S.x + Math.cos((dirLigne * Math.PI) / 180) * 40, y: S.y - Math.sin((dirLigne * Math.PI) / 180) * 40 };
        arcEgal(fig, S, P, M, 17); arcEgal(fig, S, M, Q, 17);
    }
    // sommets
    const G = { x: (S.x + P.x + Q.x) / 3, y: (S.y + P.y + Q.y) / 3 };
    [[S, noms[0]], [P, noms[1]], [Q, noms[2]]].forEach(([pt, n]) => fig.point(pt, n, angleVers(G, pt), 14));
    // étiquette (d) au bout de la droite, du côté extérieur au triangle
    if (opt.droiteVisible !== false) {
        // au bout de la droite situé au-delà de la base, à l'opposé du sommet S (dont l'étiquette est près du bord)
        let dirEtiq = dirLigne;
        if (droite === 'mediatrice') {
            const delta = (((angleVers(I, S) - dirLigne) % 360) + 540) % 360 - 180;
            dirEtiq = Math.abs(delta) < 90 ? dirLigne + 180 : dirLigne;
        }
        if (opt.nomDroite !== '') etiquetteDroite(fig, origine, dirEtiq, opt.nomDroite || '(d)');
    }
    let svg = fig.render();
    // la droite en orange : creerFigure trace en gris ardoise, on recolore la classe dr
    svg = svg.replace(/<line class="dr" ([^>]*)stroke="#334155"/g, `<line class="dr" $1stroke="${ORANGE}"`);
    svg = svg.replace('<svg ', `<svg data-droite="${droite}" data-sommet="${noms[0]}" data-cote="${noms[1]}${noms[2]}" data-config="${opt.config || 'quelconque'}" `);
    return { svg, droite, noms };
};

/** Noms tirés : le sommet principal reçoit une lettre au hasard parmi A, B, C. */
const tirerNoms = () => shuffle(['A', 'B', 'C']);

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refD = { v: null }; const refR = { v: null }; const refP = { v: null };

// ─── Carte 1 : quelle droite ? ─────────────────────────────────────────────

const construireDroite = () => {
    const droite = randomChoice(DROITES);
    const noms = tirerNoms(); const T = triangleQuelconque();
    const F = figureDroite(T, noms, droite);
    const [S, P, Q] = noms;
    const options = shuffle(DROITES.map((d) => nomDroite(d, S, P, Q)));
    const bonne = nomDroite(droite, S, P, Q);
    const payload = makeQcm(options, bonne, `<p>Dans le triangle ${[...noms].sort().join('')}, que représente la droite (d) d'après le codage ?</p>`, { ...opts(L_DROITE), elements_additionnels: imgElement(F.svg, 'medium') });
    payload.contenu = { ...payload.contenu, sousType: droite };
    payload._meta.params = { droite, sommet: S, signature: `${droite}-${S}` };
    // les trois autres choix sont les autres droites remarquables, nommées avec le même sommet ou le même côté
    const erreurs = options.filter((o) => o !== bonne).map((o) => ({ code: 'droite_remarquable_confondue', valeur: o }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genDroite = () => antiRepeat(construireDroite, refD);

// ─── Carte 1 bis : quelle droite ? (planche de quatre, même carte que le QCM) ──

const planche = (figs) => {
    const cols = 2; const rows = Math.ceil(figs.length / cols);
    const w = 210; const h = 170; const W = cols * w; const H = rows * h;
    let g = `<rect x="0" y="0" width="${W}" height="${H}" fill="#ffffff"/>`;
    figs.forEach((f, i) => {
        const vbm = f.svg.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/); const vw = Number(vbm[1]); const vh = Number(vbm[2]);
        const contenu = f.svg.replace(/^<svg [^>]*>/, '').replace(/<\/svg>\s*$/, '');
        const cc = i % cols; const r = Math.floor(i / cols);
        const sc = Math.min((w - 8) / vw, (h - 26) / vh); const tx = cc * w + (w - sc * vw) / 2; const ty = r * h + 22 + (h - 26 - sc * vh) / 2;
        g += `<g transform="translate(${tx.toFixed(1)},${ty.toFixed(1)}) scale(${sc.toFixed(4)})">${contenu}</g>`;
        g += `<text x="${cc * w + w / 2}" y="${r * h + 16}" font-family="system-ui,sans-serif" font-size="15" font-weight="800" fill="#1e3a8a" text-anchor="middle">Figure ${f.label}</text>`;
    });
    const data = figs.map((f) => `${f.label}:${f.droite}`).join('|');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" data-figures="${data}">${g}</svg>`;
};

const construireRetrouver = () => {
    const cible = randomChoice(DROITES);
    const noms = tirerNoms(); const T = triangleQuelconque();
    // figures numérotées 1 à 4 : les lettres A, B, C nomment déjà les sommets du triangle
    const lettres = ['1', '2', '3', '4'];
    const ordre = shuffle(DROITES);
    const figs = ordre.map((d, i) => ({ ...figureDroite(T, noms, d, { nomDroite: '' }), label: lettres[i] }));
    const bonne = figs.find((f) => f.droite === cible).label;
    const [S, P, Q] = noms;
    const nom = nomDroite(cible, S, P, Q); const nomMin = nom.charAt(0).toLowerCase() + nom.slice(1);
    const payload = makeQcm(lettres, bonne, `<p>Voici quatre fois le même triangle ${[...noms].sort().join('')}, avec une droite tracée à chaque fois. Sur quelle figure la droite tracée est-elle ${nomMin} ?</p>`, { ...opts(L_DROITE), elements_additionnels: imgElement(planche(figs), 'large') });
    // options dans l'ordre 1-4 ; la clé de correction est la LETTRE de position (A-D), pas le numéro (revue 2026-09-10, A1)
    payload.contenu.choix = lettres; payload.contenu.bonne_reponse = 'ABCD'[lettres.indexOf(bonne)];
    payload.contenu = { ...payload.contenu, sousType: cible };
    payload._meta.params = { cible, sommet: S, ordre, signature: `${cible}-${ordre.join(',')}` };
    // chaque autre figure porte une autre droite remarquable du même triangle
    const erreurs = figs.filter((f) => f.label !== bonne).map((f) => ({ code: 'droite_remarquable_confondue', valeur: f.label }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genRetrouver = () => antiRepeat(construireRetrouver, refR);

// ─── Carte 3 : dans un triangle particulier ────────────────────────────────

/** Triangle isocèle en S (ou équilatéral), base [PQ] horizontale, penché. */
const triangleIsocele = (equilateral) => {
    const L = 100; const h = equilateral ? (L * Math.sqrt(3)) / 2 : randomChoice([62, 70, 78, 88]);
    const pts = { S: { x: L / 2, y: -h }, P: { x: 0, y: 0 }, Q: { x: L, y: 0 } };
    const deg = randomChoice([-9, -5, 0, 4, 8]);
    return Object.fromEntries(Object.entries(pts).map(([k, v]) => [k, rot(v, deg)]));
};
/** Triangle rectangle en S : S en haut à gauche, P en bas (sous S), Q à droite de S. */
const triangleRectangle = () => {
    const a = randInt(56, 70); const b = randInt(80, 100);
    const pts = { S: { x: 0, y: 0 }, P: { x: 0, y: a }, Q: { x: b, y: 0 } };
    const deg = randomChoice([-8, -4, 0, 5, 9]);
    return Object.fromEntries(Object.entries(pts).map(([k, v]) => [k, rot(v, deg)]));
};

/** Figure d'un triangle particulier codé, avec éventuellement la droite (S I) ou rien. */
const figureParticulier = (config, noms, opt = {}) => {
    const T = config === 'rectangle' ? triangleRectangle() : triangleIsocele(config === 'equilateral');
    const cad = cadreur([T.S, T.P, T.Q], VB, 48);
    const S = cad(T.S); const P = cad(T.P); const Q = cad(T.Q);
    const fig = creerFigure(VB, `1124_${config}_${randInt(1, 999)}`);
    fig.segment(S, P); fig.segment(P, Q); fig.segment(Q, S);
    if (config === 'isocele') { traits(fig, S, P, 1); traits(fig, S, Q, 1); }
    if (config === 'equilateral') { traits(fig, S, P, 1); traits(fig, S, Q, 1); traits(fig, P, Q, 1); }
    if (config === 'rectangle') codageDroitInterieur(fig, S, P, Q, { couleur: '#0f172a', c: 10 });
    const I = milieu(P, Q);
    if (opt.avecMilieu) {
        // le milieu I est codé (deux traits, distincts du codage isocèle à un trait) et la droite (S I) est tracée
        traits(fig, P, I, 2); traits(fig, I, Q, 2);
        fig.droite(S, angleVers(S, I), 'dr', 1.8);
        // étiquette de I sous la base, décalée de la droite (S I)
        fig.point(I, opt.nomMilieu || 'I', angleVers(S, I) + 45, 15);
    }
    const G = { x: (S.x + P.x + Q.x) / 3, y: (S.y + P.y + Q.y) / 3 };
    [[S, noms[0]], [P, noms[1]], [Q, noms[2]]].forEach(([pt, n]) => fig.point(pt, n, angleVers(G, pt), 14));
    let svg = fig.render();
    svg = svg.replace(/<line class="dr" ([^>]*)stroke="#334155"/g, `<line class="dr" $1stroke="${ORANGE}"`);
    svg = svg.replace('<svg ', `<svg data-config="${config}" data-sommet="${noms[0]}" data-cote="${noms[1]}${noms[2]}" data-milieu="${opt.avecMilieu ? (opt.nomMilieu || 'I') : ''}" `);
    return svg;
};

const construireParticulier = () => {
    const config = randomChoice(['isocele', 'equilateral', 'rectangle']);
    const noms = tirerNoms(); const [S, P, Q] = noms; const nomT = [...noms].sort().join('');
    let texte; let bonne; let leurres; let svg;
    if (config === 'rectangle') {
        // la hauteur issue de P est le côté [PS] (angle droit en S)
        svg = figureParticulier(config, noms);
        texte = `<p>Le triangle ${nomT} est rectangle en ${S}. Quelle est la hauteur issue de ${P} ?</p>`;
        bonne = `La droite (${seg(P, S)})`;
        leurres = [`La droite (${seg(P, Q)})`, `La médiane issue de ${P}`, `La médiatrice du segment [${seg(S, Q)}]`];
    } else {
        svg = figureParticulier(config, noms, { avecMilieu: true, nomMilieu: 'I' });
        const quoi = config === 'isocele' ? `est isocèle en ${S}` : 'est équilatéral';
        texte = `<p>Le triangle ${nomT} ${quoi} et I est le milieu de [${seg(P, Q)}]. D'après le codage, que peut-on dire de la droite (${S}I) ?</p>`;
        bonne = `C'est à la fois la médiane et la hauteur issues de ${S}, la médiatrice de [${seg(P, Q)}] et la bissectrice de l'angle ${P}${S}${Q}.`;
        leurres = [
            // deux leurres aussi longs que la bonne réponse (revue 2026-09-10, A2 : la bonne n'est plus « la plus longue »)
            `C'est seulement la médiane issue de ${S}.`,
            `C'est la médiane issue de ${S} et la médiatrice de [${seg(P, Q)}], mais ce n'est ni la hauteur issue de ${S} ni la bissectrice de l'angle ${P}${S}${Q}.`,
            `C'est la hauteur issue de ${S} et la bissectrice de l'angle ${P}${S}${Q}, mais pas la médiane issue de ${S}.`,
        ];
    }
    const options = shuffle([bonne, ...leurres]);
    const payload = makeQcm(options, bonne, texte, { ...opts(L_PARTICULIER), elements_additionnels: imgElement(svg, 'medium') });
    payload.contenu = { ...payload.contenu, sousType: config };
    payload._meta.params = { config, sommet: S, signature: `${config}-${S}` };
    // isocèle, équilatéral : les droites confondues pas vues ; rectangle : une autre droite remarquable prise pour la hauteur (le côté (PQ) : sans code)
    const codeLeurre = (l) => (config !== 'rectangle' ? 'triangle_particulier_ignore' : l.startsWith('La droite') ? null : 'droite_remarquable_confondue');
    const erreurs = leurres.map((l) => ({ code: codeLeurre(l), valeur: l })).filter((x) => x.code);
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genParticulier = () => antiRepeat(construireParticulier, refP);

// ─── Carte 4 : vrai ou faux ? (sur une figure codée) ───────────────────────

let derniereEmpreinteVf1124 = '';

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const droite = randomChoice(DROITES);
        const noms = tirerNoms(); const [S, P, Q] = noms; const T = triangleQuelconque();
        const F = figureDroite(T, noms, droite);
        const autre = randomChoice(DROITES.filter((d) => d !== droite));
        const parSommet = droite !== 'mediatrice'; const perp = droite === 'hauteur' || droite === 'mediatrice'; const parMilieu = droite === 'mediane' || droite === 'mediatrice';
        const nomMin = (d) => { const n = nomDroite(d, S, P, Q); return n.charAt(0).toLowerCase() + n.slice(1); };
        const familles = [
            { f: 'nature', vrai: `(d) est ${nomMin(droite)}.`, faux: `(d) est ${nomMin(autre)}.` },
            { f: 'sommet', vrai: parSommet ? `(d) passe par le sommet ${S}.` : `(d) ne passe pas par le sommet ${S}.`, faux: parSommet ? `(d) ne passe pas par le sommet ${S}.` : `(d) passe par le sommet ${S}.` },
            { f: 'perp', vrai: perp ? `D'après le codage, (d) est perpendiculaire à (${seg(P, Q)}).` : `Le codage ne dit pas que (d) est perpendiculaire à (${seg(P, Q)}).`, faux: perp ? `Le codage ne dit pas que (d) est perpendiculaire à (${seg(P, Q)}).` : `D'après le codage, (d) est perpendiculaire à (${seg(P, Q)}).` },
            { f: 'milieu', vrai: parMilieu ? `D'après le codage, (d) passe par le milieu de [${seg(P, Q)}].` : `Le codage ne dit pas que (d) passe par le milieu de [${seg(P, Q)}].`, faux: parMilieu ? `Le codage ne dit pas que (d) passe par le milieu de [${seg(P, Q)}].` : `D'après le codage, (d) passe par le milieu de [${seg(P, Q)}].` },
            { f: 'angle', vrai: droite === 'bissectrice' ? `D'après le codage, (d) partage l'angle ${P}${S}${Q} en deux angles de même mesure.` : `Le codage ne dit pas que (d) partage l'angle ${P}${S}${Q} en deux angles de même mesure.`, faux: droite === 'bissectrice' ? `Le codage ne dit pas que (d) partage l'angle ${P}${S}${Q} en deux angles de même mesure.` : `D'après le codage, (d) partage l'angle ${P}${S}${Q} en deux angles de même mesure.` },
        ];
        const retenues = [familles[0], ...shuffle(familles.slice(1)).slice(0, 3)];
        const nbFaux = randInt(1, 3);
        const ordre = shuffle(retenues); const lignes = ordre.map((fa, i) => (i < nbFaux ? { l: fa.faux, v: false, f: fa.f } : { l: fa.vrai, v: true, f: fa.f }));
        const empreinte = lignes.map((it) => it.l).sort().join('#');
        if (empreinte === derniereEmpreinteVf1124) continue;
        derniereEmpreinteVf1124 = empreinte;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(
            melange.map((it) => it.l),
            melange.map((it) => (it.v ? 0 : 1)),
            `<p>Dans le triangle ${[...noms].sort().join('')}, la droite (d) est tracée et codée. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`,
            { ...opts(L_VF), elements_additionnels: imgElement(F.svg, 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { droite, sommet: S, nbFaux };
        // une droite prise pour une autre ; nier ce que dit le codage, ou lui faire dire ce qu'il ne dit pas
        const codeFaux = {
            nature: 'droite_remarquable_confondue',
            sommet: 'droite_remarquable_confondue',
            perp: perp ? 'refus_du_codage' : 'lit_la_figure_pas_le_codage',
            milieu: parMilieu ? 'refus_du_codage' : 'lit_la_figure_pas_le_codage',
            angle: droite === 'bissectrice' ? 'refus_du_codage' : 'lit_la_figure_pas_le_codage',
        };
        const erreurs = melange.filter((it) => !it.v).map((it) => ({ code: codeFaux[it.f], ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1124 : V/F sans tirage après 160 essais');
};

// ─── Carte 5 : les droites remarquables, vrai ou faux ? (pur rappel) ───────

// Lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md) : les définitions confondues et les hauteurs du triangle rectangle
// ont leurs propres codes.
const POOL_RAPPEL = [
    { l: 'Une hauteur d\'un triangle passe par un sommet et est perpendiculaire au côté opposé.', v: true, f: 'hauteur' },
    { l: 'Une hauteur d\'un triangle passe toujours par le milieu du côté opposé.', v: false, f: 'hauteur', code: 'definitions_des_droites_confondues' },
    { l: 'Une médiane d\'un triangle passe par un sommet et par le milieu du côté opposé.', v: true, f: 'mediane' },
    { l: 'Une médiane d\'un triangle est toujours perpendiculaire au côté opposé.', v: false, f: 'mediane', code: 'definitions_des_droites_confondues' },
    { l: 'La médiatrice d\'un côté est perpendiculaire à ce côté en son milieu.', v: true, f: 'mediatrice' },
    { l: 'La médiatrice d\'un côté passe toujours par le sommet opposé.', v: false, f: 'mediatrice', code: 'definitions_des_droites_confondues' },
    { l: 'La bissectrice d\'un angle le partage en deux angles de même mesure.', v: true, f: 'bissectrice' },
    { l: 'La bissectrice d\'un angle du triangle passe par le milieu du côté opposé.', v: false, f: 'bissectrice', code: 'definitions_des_droites_confondues' },
    { l: 'Dans un triangle isocèle, la hauteur issue du sommet principal est aussi la médiane, la médiatrice de la base et la bissectrice.', v: true, f: 'isocele' },
    { l: 'Dans un triangle isocèle, les trois hauteurs sont aussi des médianes.', v: false, f: 'isocele', code: 'cas_particulier_generalise' },
    { l: 'Dans un triangle équilatéral, les hauteurs, les médianes, les médiatrices et les bissectrices sont confondues.', v: true, f: 'equilateral' },
    { l: 'Dans un triangle équilatéral, les hauteurs et les médianes sont des droites différentes.', v: false, f: 'equilateral', code: 'triangle_particulier_ignore' },
    { l: 'Dans un triangle rectangle, deux hauteurs sont des côtés du triangle.', v: true, f: 'rectangle' },
    { l: 'Dans un triangle rectangle, les trois hauteurs sont à l\'intérieur du triangle.', v: false, f: 'rectangle', code: 'hauteurs_du_triangle_rectangle' },
    { l: 'Les trois hauteurs d\'un triangle sont concourantes.', v: true, f: 'concours' },
    { l: 'Les trois médianes d\'un triangle ne se coupent jamais en un même point.', v: false, f: 'concours', code: 'concours_nie' },
];

let dernieresLignesRappel1124 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1124 = [];
        if (lignes.some((it) => dernieresLignesRappel1124.includes(it.l))) continue;
        dernieresLignesRappel1124 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur les droites remarquables d\'un triangle, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        // le code de chaque affirmation fausse du vivier
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1124 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    droite_qcm_unique: genDroite,
    droite_planche_qcm_unique: genRetrouver,
    particulier_qcm_unique: genParticulier,
    vf_tableau_vf: genVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', planche_qcm_unique: 'QCM planche', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'droite', label: L_DROITE, formatsList: buildFormats('droite') },
    { id: 'particulier', label: L_PARTICULIER, formatsList: buildFormats('particulier') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    particulier_qcm_unique: ['droite_remarquable_confondue'],
    vf_tableau_vf: ['refus_du_codage', 'lit_la_figure_pas_le_codage'],
    // relecture du 04/10/2026 (juge du 03/10) : « les trois hauteurs du triangle isocèle » sort aussi
    rappel_tableau_vf: ['concours_nie', 'cas_particulier_generalise'],
    droite_planche_qcm_unique: ['droite_remarquable_confondue'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { DROITES, nomDroite, figureDroite, figureParticulier, triangleQuelconque, POOL_RAPPEL };
